import React, { useState, useMemo, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import mapboxgl from 'mapbox-gl'
// eslint-disable-next-line import/no-webpack-loader-syntax
import MapboxWorker from 'worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker'
import ReactMapboxGl, { ZoomControl, Marker } from 'react-mapbox-gl'
import {
  ZOOM_SHORT_NAME
} from '../../filters/mapbox/mapboxConfig'
import {
  LONGITUDE_SHORT_NAME, LATITUDE_SHORT_NAME,
  NE_LATITUDE_SHORT_NAME, NE_LONGITUDE_SHORT_NAME,
  SW_LATITUDE_SHORT_NAME, SW_LONGITUDE_SHORT_NAME,
  LOCATION_SHORT_NAME
} from '../../filters/location/locationFiltersConfig'
import {
  MAX_DISTANCE_FILTER_SHORT_NAME
} from '../../filters/moreFiltersContainer/distanceFilterConfig'
import 'mapbox-gl/dist/mapbox-gl.css'
import { MAPBOX_API_KEY } from '../../../../constants'
import MapMarker from './searchMapTag.js'
import { hideAll } from 'tippy.js'
import ErrorBoundary from '../errors/errorBound.js'
import './mapStyles.css'
import { debounce } from 'lodash'
import { getPlaceName } from './reverseGeoCode'

const MAX_RADIUS = 300
const MIN_ZOOM = 5

const turf = require('@turf/turf')
mapboxgl.workerClass = MapboxWorker

const Map = ReactMapboxGl({
  accessToken: MAPBOX_API_KEY,
  trackResize: true,
  attributionControl: false
})

const SearchMapBox = (props) => {
  const {
    searchResults, allCoordinates,
    hasMapboxSearchData, groupedMarkers,
    hasGooseEgg
  } = props

  const query = new URLSearchParams(useLocation().search);

  const [coordinates, setCoordinates] = useState({
    latitude: props.center.lat,
    longitude: props.center.lng,
  })

  const boundingBox = useMemo(() => getBoundingBox(), [allCoordinates, query])
  const fitBoundsOptions = useMemo(() => ({ duration: 0 }), [])

  const onMarkerOpen = () => hideAll({ duration: 0 })
  const onMarkerClose = () => hideAll({ duration: 0 })
  const isFocused = (bikeOwnerID) => bikeOwnerID === props.focusedResult
  const canLoadMap = () => !(isNaN(coordinates.latitude) || isNaN(coordinates.longitude))

  function defaultBoundingBox() {
    // Prepare bbox for initial fitBounds
    let totalCoordinates = allCoordinates.length
    let validCoordinates = totalCoordinates === 1
      ? [allCoordinates[0], allCoordinates[0]]
      : totalCoordinates > 1
        ? allCoordinates
        : [[coordinates.longitude, coordinates.latitude], [coordinates.longitude, coordinates.latitude]]

    let line = turf.lineString(validCoordinates)
    let bbox = turf.bbox(line)
    return [
      [Number(bbox[0]), Number(bbox[1])],
      [ Number(bbox[2]), Number(bbox[3])]
    ]
  }
  function getBoundingBox() {
    if (typeof query.get == "function") {
      let neLat = Number(query.get("ne_lat"))
      let neLng = Number(query.get("ne_lng"))
      let swLat = Number(query.get("sw_lat"))
      let swLng = Number(query.get("sw_lng"))
      if (neLat && neLng && swLat && swLng) {
        return [
          [swLng, swLat], //southWest
          [neLng, neLat] //northEast
        ]
      } else {
        return defaultBoundingBox()
      }
    } else {
      return defaultBoundingBox()
    }
  }

  // Handle map pan/zoom
  const handleMapChange = useCallback(async (map) => {
    const bounds = map.getBounds()
    const ne = bounds.getNorthEast()
    const sw = bounds.getSouthWest()
    const zoom = map.getZoom()

    // center point
    const center = map.getCenter()
    const placeName = await getPlaceName(center.lat, center.lng)

    // Four edges (N, S, E, W midpoints)
    const north = [center.lng, ne.lat]
    const south = [center.lng, sw.lat]
    const east  = [ne.lng, center.lat]
    const west  = [sw.lng, center.lat]

    let  distances = [
        turf.distance([center.lng, center.lat], north, { units: 'miles' }),
        turf.distance([center.lng, center.lat], south, { units: 'miles' }),
        turf.distance([center.lng, center.lat], east,  { units: 'miles' }),
        turf.distance([center.lng, center.lat], west,  { units: 'miles' })
      ]

    let radius = Math.min(...distances)
    radius = Math.min(Math.floor(radius - 3), MAX_RADIUS)
    radius = Math.floor(radius)

    // Now build circle
    const circle = turf.circle([center.lng, center.lat], radius, {
      steps: 64,
      units: 'miles'
    })

    // Add or update GeoJSON source for circle
    if (map.getSource('radius-circle')) {
      map.getSource('radius-circle').setData(circle)
    } else {
      map.addSource('radius-circle', {
        type: 'geojson',
        data: circle
      })

      map.addLayer({
        id: 'radius-circle-layer',
        type: 'fill',
        source: 'radius-circle',
        layout: {},
        paint: {
          'fill-color': '#088',
          'fill-opacity': 0.2
        }
      })

      map.addLayer({
        id: 'radius-circle-outline',
        type: 'line',
        source: 'radius-circle',
        layout: {},
        paint: {
          'line-color': '#088',
          'line-width': 2
        }
      })
    }

    let newFilters = {
      [LONGITUDE_SHORT_NAME]: center.lng,
      [LATITUDE_SHORT_NAME]: center.lat,
      [LOCATION_SHORT_NAME]: placeName || "Nowhere",
      [ZOOM_SHORT_NAME]: zoom,
      [NE_LATITUDE_SHORT_NAME]: ne.lat,
      [NE_LONGITUDE_SHORT_NAME]: ne.lng,
      [SW_LATITUDE_SHORT_NAME]: sw.lat,
      [SW_LONGITUDE_SHORT_NAME]: sw.lng,
      [MAX_DISTANCE_FILTER_SHORT_NAME]:  radius
    }

    if (hasGooseEgg) {
      newFilters["goose_egg"] = true
    }

    props.onMapMove(newFilters)
  }, [props.onMapMove])

  // Debounce to avoid spamming API
  const handleMapChangeDebounced = useMemo(
    () => debounce(handleMapChange, 200),
    [handleMapChange]
  )

  const CustomMarker = () => (
    hasMapboxSearchData &&
    groupedMarkers?.map((result, index) => (
      <Marker
        style={{ width: 20, height: 20 }}
        coordinates={[result[0].bike_location.longitude, result[0].bike_location.latitude]}
        anchor="bottom"
        key={`marker-${index}-${result[0].bike_id}`}
      >
        <MapMarker
          info={result}
          index={index}
          key={index}
          lat={result[0].bike_location.latitude}
          lng={result[0].bike_location.longitude}
          onOpen={onMarkerOpen}
          onClose={onMarkerClose}
          focused={isFocused(result[0].bike_owner_id)}
          onHover={props.onResultHover}
          startDate={props.startDate}
          endDate={props.endDate}
          searchResults={searchResults}
        />
      </Marker>
    ))
  )

  return (
    <>
      {canLoadMap() && (
        <ErrorBoundary>
          <Map
            style="mapbox://styles/mapbox/streets-v9"
            containerStyle={{
                height: '100%',
                width: '100%',
              }}
            // center={[props.center.lng, props.center.lat]}
            // zoom={[zoom]}
            // movingMethod="flyTo"
            onDragEnd={(map) => {
              if (!props.onMapMove) {
                return
              }
              handleMapChangeDebounced(map) // still handle drag if needed
            }}
            onZoomEnd={(map, event) => {
              if (!props.onMapMove) {
                return
              }
              // Only trigger when user zooms (mouse wheel, pinch, control click, etc.)
              // when new results are integraded, mapbox updates fibounds. In this case handleMapChangeDebounced shouldn't be called.
              if (event && !event.fitboundUpdate) {
                handleMapChangeDebounced(map)
              }
            }}
            fitBounds={boundingBox}
            fitBoundsOptions={fitBoundsOptions}
          >
            <ZoomControl
              position="top-left"

            />
              {CustomMarker()}
          </Map>
        </ErrorBoundary>
      )}
    </>
  )
}

export default SearchMapBox

SearchMapBox.defaultProps = {
  isMobile: true,
  focusedResult: null,
  onResultHover: () => {},
  startDate: null,
  endDate: null,
}
